/**
 * Embudo Lean Startup (stacked %) — Analytics → Producto.
 * RPC: get_product_lean_funnel_analytics()
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';

export type LeanFunnelStageKey =
    | 'no_session'
    | 'connected'
    | 'onboarding'
    | 'first_capture'
    | 'retained'
    | 'still_active'
    | 'pro';

export interface LeanFunnelCheckpoint {
    ord: number;
    key: string;
    label: string;
    age_days: number;
    cohort_n: number;
    stages: Record<LeanFunnelStageKey, number>;
    counts: Record<LeanFunnelStageKey, number>;
}

export interface LeanFunnelWindow {
    start: string;
    end: string;
    label: string;
    cohort_n: number;
}

export interface ProductLeanFunnelAnalytics {
    current_window: LeanFunnelWindow;
    previous_window: LeanFunnelWindow;
    stage_order: LeanFunnelStageKey[];
    stage_labels: Record<LeanFunnelStageKey, string>;
    current: LeanFunnelCheckpoint[];
    previous: LeanFunnelCheckpoint[];
}

function asArray<T>(v: unknown): T[] {
    if (Array.isArray(v)) return v as T[];
    return [];
}

function parseResponse(data: unknown): ProductLeanFunnelAnalytics | null {
    const root = Array.isArray(data) ? data[0] : data;
    if (!root || typeof root !== 'object') return null;
    const o = root as Record<string, unknown>;
    if (!o.current_window || !o.stage_order) return null;
    return {
        current_window: o.current_window as LeanFunnelWindow,
        previous_window: o.previous_window as LeanFunnelWindow,
        stage_order: o.stage_order as LeanFunnelStageKey[],
        stage_labels: o.stage_labels as Record<LeanFunnelStageKey, string>,
        current: asArray<LeanFunnelCheckpoint>(o.current),
        previous: asArray<LeanFunnelCheckpoint>(o.previous),
    };
}

/** Último checkpoint presente en ambas series (p. ej. Semana 8 si la cohorte actual aún no tiene 70 días). */
export function lastComparableCheckpoint(
    current: LeanFunnelCheckpoint[],
    previous: LeanFunnelCheckpoint[],
): LeanFunnelCheckpoint | null {
    if (!current.length || !previous.length) return null;
    const prevKeys = new Set(previous.map((p) => p.key));
    const shared = current.filter((c) => prevKeys.has(c.key));
    return shared[shared.length - 1] ?? null;
}

export function useProductLeanFunnel() {
    return useQuery<ProductLeanFunnelAnalytics>({
        queryKey: ['admin', 'product-lean-funnel'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_product_lean_funnel_analytics');
            if (error) throw error;
            const parsed = parseResponse(data);
            if (!parsed) {
                throw new Error('Respuesta inválida de get_product_lean_funnel_analytics');
            }
            return parsed;
        },
        staleTime: 5 * 60_000,
        retry: 1,
    });
}
